// src/pages/AdminConfig.tsx
import React, { useEffect, useState, useRef } from "react";

const AI_API_URL = process.env.REACT_APP_AI_API_URL;
const API = `${AI_API_URL}/admin`;
const HEADERS = { "x-api-key": "change-iris" };

const MOD_API = `${AI_API_URL}/admin/moderation-config`;
type ModRow = { channel: string; rule: string };

type Channel = {
  name: string;
  hls_url: string;
};

export default function AdminConfig() {
  /* ───────────────────────── JSON text ───────────────────────── */
  const [text, setText] = useState<string>("{}");
  const [cfgDirty, setCfgDirty] = useState(false);

  /* ─────────────────────── transcriber ui ────────────────────── */
  const [transEnabled, setTransEnabled] = useState(false);  // checkbox
  const [remoteRunning, setRemoteRunning] = useState(false); // backend
  const [loading, setLoading] = useState(true);             // while first fetches run

  /* ─────────────────────── UI helper ─────────────────────────── */
  const [msg, setMsg] = useState("");


  const [channels, setChannels] = useState<Channel[]>([]);
  // We’ll only display the transcript for the first channel in the list,
  // but you can expand this to show all channels if you like.
  const [currentTranscript, setCurrentTranscript] = useState<string>("");
  const [transLoading, setTransLoading] = useState<boolean>(false);
  const pollingRef = useRef<number | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);


  const [modRows, setModRows] = useState<ModRow[]>([]);
  const [modDirty, setModDirty] = useState(false);


  const markModDirty = () => { setModDirty(true); setMsg(""); };
  const addEmptyRow = () => {
    setModRows((r) => [...r, { channel: "", rule: "" }]);
    markModDirty();
  };
  const deleteRow = (idx: number) => {
    setModRows((r) => r.filter((_, i) => i !== idx));
    markModDirty();
  };
  const canSaveMod = !loading && modDirty;


  const onSaveMod = async () => {
    setMsg("");
    /* build payload */
    const payload = {
      active_channels: modRows.reduce<Record<string, string>>((acc, { channel, rule }) => {
        if (channel.trim()) acc[channel.trim()] = rule.trim();
        return acc;
      }, {}),
    };

    try {
      const put = await fetch(MOD_API, {
        method: "PUT",
        headers: { ...HEADERS, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!put.ok) throw new Error(await put.text());
      setModDirty(false);
      setMsg("✅ Saved moderation_config.json");
    } catch (e: any) {
      setMsg(`❌ Moderation save failed: ${e.message || "unknown error"}`);
    }
  };

  /* ─────────────── initial fetches (config + status) ─────────── */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/config`, { headers: HEADERS });
        const json = await res.json();
        setText(JSON.stringify(json, null, 2));
        setChannels(json.channels);
      } catch {
        setMsg("❌ Could not load config.json");
        setChannels([]);
      }

      /* moderation_config.json */
      try {
        const res = await fetch(`${MOD_API}`, { headers: HEADERS });
        const j = await res.json();                    // {active_channels:{}}
        const rows: ModRow[] = Object.entries(j.active_channels || {}).map(
          ([channel, rule]) => ({ channel, rule: String(rule) })
        );
        setModRows(rows);
      } catch {
        setMsg("❌ Could not load moderation_config.json");
      }


      try {
        const res = await fetch(`${API}/transcribers/status`, {
          headers: HEADERS,
        });
        if (res.ok) {
          const { running } = await res.json();
          setRemoteRunning(running);
          setTransEnabled(running); // keep checkbox in sync
        }
      } catch {
        /* ignore – leave defaults */
      } finally {
        setLoading(false);
      }
    })();
  }, []);


    /* ─────────────── poll transcript every 5 seconds ─────────────── */
  useEffect(() => {
    // Only start polling once we know which channel(s) exist
    if (channels.length === 0) return () => {};

    const firstChannel = channels[0];
    if (!firstChannel?.hls_url) return () => {};

    async function fetchTranscript() {
      setTransLoading(true);
      try {
        const url = new URL(`${API}/transcript`);
        console.log(firstChannel.hls_url);
        url.searchParams.append("hls_url", firstChannel?.hls_url);
        const res = await fetch(url.toString(), {
          headers: HEADERS,
        });
        if (!res.ok) {
          setCurrentTranscript(`<error: ${res.statusText}>`);
        } else {
          const j = await res.json();
          setCurrentTranscript(j.transcript || "");
          setLastFetchedAt(new Date());
        }
      } catch {
        setCurrentTranscript("<failed to fetch>");
        setLastFetchedAt(new Date());
      } finally {
        setTransLoading(false);
      }
    }

    // Immediately fetch once, then every 5 seconds
    fetchTranscript();
    pollingRef.current = window.setInterval(fetchTranscript, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [channels]);

  /* ───────── checkbox toggle – mark page “dirty” ─────────────── */
  const toggleTrans = () => {
    setTransEnabled((prev) => !prev);
  };

  /* save button active if json dirty OR checkbox differs */
  const canSave = !loading && (cfgDirty || transEnabled !== remoteRunning);

  /* ───────────────────────── onSave ───────────────────────────── */
  const onSave = async () => {
    setMsg("");                      // clear previous status

    /* 1️⃣  update config.json if needed */
    if (cfgDirty) {
      try {
        const parsed = JSON.parse(text);
        const put = await fetch(`${API}/config`, {
          method: "PUT",
          headers: { ...HEADERS, "Content-Type": "application/json" },
          body: JSON.stringify(parsed),
        });
        if (!put.ok) throw new Error(await put.text());
        setCfgDirty(false);
        setMsg("✅ Saved config.json");
      } catch (e: any) {
        setMsg(`❌ Save failed: ${e.message || "unknown error"}`);
        return;
      }
    }

    /* 2️⃣  re-check server-side status just before acting */
    let runningNow = false;
    try {
      const r = await fetch(`${API}/transcribers/status`, {
        headers: HEADERS,
      });
      if (r.ok) {
        const j = await r.json();
        runningNow = !!j.running;
      }
    } catch {
      /* assume false */
    }

    /* 3️⃣  start or stop only if necessary */
    try {
      if (transEnabled && !runningNow) {
        const r = await fetch(`${API}/transcribers/start`, {
          method: "POST",
          headers: HEADERS,
        });
        if (!r.ok) throw new Error(await r.text());
        const j = await r.json();               // {"status":"started …"} OR {"status":"already running"}
        setMsg(`✅ ${j.status}`);
        runningNow = true;
      }

      if (!transEnabled && runningNow) {
        const r = await fetch(`${API}/transcribers/stop`, {
          method: "POST",
          headers: HEADERS,
        });
        if (!r.ok) throw new Error(await r.text());
        const j = await r.json();               // {"status":"stopped …"}
        setMsg(`✅ ${j.status}`);
        runningNow = false;
      }
    } catch (e: any) {
      setMsg(`❌ ${e.message || "action failed"}`);
    }

    /* sync local flag with reality */
    setRemoteRunning(runningNow);
  };

  /* ───────────────────────── render ───────────────────────────── */
  return (
    <div style={{ maxWidth: 800, margin: "40px auto", fontFamily: "sans-serif" }}>
      <h2>AI-Server Config</h2>

      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setCfgDirty(true);
          setMsg("");
        }}
        rows={24}
        spellCheck={false}
        style={{
          width: "100%",
          fontFamily: "monospace",
          fontSize: 14,
          background: "#1e1e1e",
          color: "#dcdcdc",
          border: "1px solid #444",
          borderRadius: 4,
          padding: 12,
          boxSizing: "border-box",
          whiteSpace: "pre",
        }}
      />

      {/* transcriber toggle */}
      <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
        <label
          htmlFor="transcriberEnabled"                         // ★ associate
          style={{ color: "#ddd", display: "flex", alignItems: "center", gap: 6 }}
        >
          <input
            id="transcriberEnabled"                            // ★ referenced
            type="checkbox"
            checked={transEnabled}
            disabled={loading}
            onChange={() => setTransEnabled((p) => !p)}
          />
          Transcriber&nbsp;Enabled
        </label>

          <div
          style={{
            flex: 1,
            background: "#f9f9f9",
            border: "1px solid #ccc",
            borderRadius: 4,
            padding: 8,
            maxHeight: 120,
            overflowY: "auto",
            fontFamily: "monospace",
            fontSize: 13,
            color: "#222",
          }}
        >
       <pre
         style={{
           whiteSpace: "pre-wrap",
           wordBreak: "break-word",
           margin: 0,
           minHeight: "1em",        /* keep the box from collapsing */
           opacity: transLoading   /* subtly fade while loading */
             ? 0.6
             : 1,
         }}
       >
         {currentTranscript || (transLoading ? "<loading…>" : "<no transcript yet>")}
       </pre>
        </div>
            {lastFetchedAt && (
            <div style={{ marginBottom: 4, fontSize: 12, color: "#666" }}>
              Last fetched at:{" "}
              {lastFetchedAt.toLocaleTimeString(undefined, {
                hour12: false,
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </div>
          )}
      </div>

      {loading && <span style={{ color: "#aaa" }}>Loading status…</span>}

      {/* save button */}
      <div style={{ marginTop: 12, display: "flex", gap: 12 }}>
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave}
          style={{
            padding: "6px 14px",
            background: canSave ? "#0078d4" : "#666",
            color: "white",
            border: "none",
            borderRadius: 4,
            cursor: canSave ? "pointer" : "default",
          }}
        >
          Save
        </button>
        <span style={{ alignSelf: "center", color: "#aaa" }}>{msg}</span>
      </div>


      <h2 style={{ marginTop: 50 }}>Content Moderation</h2>

      <div style={{
        border: "1px solid #444", borderRadius: 6, overflow: "hidden",
        fontSize: 14, color: "#ddd"
      }}>
        {/* header row */}
        <div style={{ display: "grid",
                      gridTemplateColumns: "160px 1fr 32px",
                      background: "#272727", padding: "8px 12px",
                      fontWeight: 600 }}>
          <span>Active Channels</span>
          <span>Custom Channel Rules</span>
          <span />
        </div>

        {/* data rows */}
        {modRows.map((row, idx) => (
          <div key={idx} style={{
            display: "grid", gridTemplateColumns: "160px 1fr 32px",
            padding: "8px 12px", borderTop: "1px solid #333"
          }}>
            <input
              value={row.channel}
              placeholder="e.g. bloombergtvplus"
              onChange={(e) => {
                const v = e.target.value;
                setModRows((rows) =>
                  rows.map((r, i) => i === idx ? { ...r, channel: v } : r)
                );
                markModDirty();
              }}
              style={{
                width: "100%", fontFamily: "monospace",
                background: "#1e1e1e", color: "#dcdcdc", border: "1px solid #555",
                borderRadius: 4, padding: "4px 6px", boxSizing: "border-box"
              }}
            />
            <textarea
              value={row.rule}
              placeholder="custom moderation rule (optional)"
              onChange={(e) => {
                const v = e.target.value;
                setModRows((rows) =>
                  rows.map((r, i) => i === idx ? { ...r, rule: v } : r)
                );
                markModDirty();
              }}
              rows={2}
              style={{
                width: "100%", fontFamily: "monospace", resize: "vertical",
                background: "#1e1e1e", color: "#dcdcdc", border: "1px solid #555",
                borderRadius: 4, padding: "4px 6px", boxSizing: "border-box"
              }}
            />
            <button type="button"
              onClick={() => deleteRow(idx)}
              title="Delete row"
              style={{
                background: "none", border: "none", color: "#ff5555",
                cursor: "pointer", fontSize: 18, lineHeight: 1
              }}
            >x</button>
          </div>
        ))}

        {/* add‑row button */}
        <button type="button"
          onClick={addEmptyRow}
          style={{
            width: "100%", textAlign: "center", padding: 10,
            background: "#222", color: "#00b7ff", border: "none",
            borderTop: "1px solid #333", cursor: "pointer",
            fontWeight: 600
          }}
        >
          + Add Channel
        </button>
      </div>

      {/* save */}
      <div style={{ marginTop: 12, display: "flex", gap: 12 }}>
        <button
          type="button"
          onClick={onSaveMod}
          disabled={!canSaveMod}
          style={{
            padding: "6px 14px",
            background: canSaveMod ? "#0078d4" : "#666",
            color: "white",
            border: "none",
            borderRadius: 4,
            cursor: canSaveMod ? "pointer" : "default",
          }}
        >
          Save Moderation
        </button>
      </div>
    </div>
  );
}
